import { Flex } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ActionButton } from '../../../components/ActionButton'
import { BackLink } from '../../../components/BackLink'
import { LoadingBlock } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { useAppToast, useDownload, useHasRole } from '../../../helpers'
import { EventEstablishmentService } from '../../../services/Event/EventEstablishmentService'
import { EventService } from '../../../services/Event/EventService'
import { EstablishmentSalesSection } from './establishments/EstablishmentSalesSection'

/**
 * Tela "Vendas dos Parceiros" do evento (ABRA-317), aberta pelo botão "Vendas de parceiros" ao lado
 * de "Compras" na página do evento: o relatório das vendas dos estabelecimentos, a lista detalhada e
 * a exportação. A parte da ABRA nessas vendas continua no card de receita da página do evento.
 */
export default function EventEstablishmentSalesPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const { isDownloading, download } = useDownload()
  const establishments = useMemo(() => new EventEstablishmentService(), [])

  const [eventId, setEventId] = useState<number | null>(null)

  useEffect(() => {
    new EventService()
      .getBySlug(slug!)
      .then((event) => setEventId(event.id))
      .catch(() => {
        toast.error('Evento não encontrado')
        navigate('/events')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  return (
    <>
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mb={5}
      >
        <BackLink to={`/events/${slug}`}>Voltar para o Evento</BackLink>
      </Flex>

      <PageTitle
        title="Vendas dos Parceiros"
        description="Vendas dos estabelecimentos parceiros no evento"
        mb={5}
        actions={
          <ActionButton
            action="download"
            label="Exportar XLSX"
            showLabel
            mr={0}
            isLoading={isDownloading}
            isDisabled={eventId === null}
            onClick={() =>
              eventId !== null &&
              download(() => establishments.downloadXlsx(eventId, slug!), 'Erro ao exportar as vendas dos parceiros')
            }
          />
        }
      />

      {eventId === null ? (
        <LoadingBlock entity="vendas dos parceiros" />
      ) : (
        // Cancelar uma venda exige `edit_event`: quem edita o evento cuida dos parceiros.
        <EstablishmentSalesSection eventId={eventId} canCancel={hasRole('edit_event')} />
      )}
    </>
  )
}
